import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Check, X, Clock, MessageCircle, Printer, Plus, IndianRupee, FileText, Trash2, Pencil, RotateCcw, Download } from "lucide-react";

const STATUS_STYLES = {
  pending: "bg-[hsl(var(--brass)/0.15)] text-[hsl(var(--brass-2))]",
  confirmed: "bg-[hsl(148_40%_88%)] text-[hsl(var(--forest))]",
  declined: "bg-[hsl(6_60%_92%)] text-[hsl(6_62%_45%)]",
  cancelled: "bg-[hsl(var(--rule))] text-[hsl(var(--ink-soft))]",
};

const METHOD_OPTIONS = [
  { value: "upi", label: "UPI" },
  { value: "bank", label: "Bank Transfer" },
  { value: "cash", label: "Cash" },
  { value: "card", label: "Card" },
  { value: "other", label: "Other" },
];

const EMPTY_FORM = { name: "", phone: "", email: "", date: "", time: "19:30", party_size: 2, occasion: "", notes: "", setup: "TBC", confirm_by: "" };
const EMPTY_ADV = { amount: "", method: "upi", reference: "", notes: "" };

// A declined booking's Confirm button is hidden until the admin edits it
// (any edit bumps updated_at; an "edit" is considered anything that sets
// updated_at strictly AFTER declined_at). Reopen is a separate action.
const canConfirm = (r) => {
  if (r.status === "confirmed") return false;
  if (r.status !== "declined") return true;
  if (!r.declined_at) return true;
  if (!r.updated_at) return false;
  return new Date(r.updated_at).getTime() > new Date(r.declined_at).getTime();
};

const fmtTs = (v) => {
  if (!v) return "";
  try {
    return new Date(v).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return String(v); }
};

export default function StaffReservations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null); // reservation id being edited
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [advTarget, setAdvTarget] = useState(null); // reservation object
  const [advForm, setAdvForm] = useState(EMPTY_ADV);
  const [advSaving, setAdvSaving] = useState(false);
  const [receiptDownloadId, setReceiptDownloadId] = useState(null);
  // Event Order popover — small date-range picker to download the kitchen &
  // floor briefing PDF for a chosen window.
  const [eoOpen, setEoOpen] = useState(false);
  const [eoDownloading, setEoDownloading] = useState(false);
  const [eoRange, setEoRange] = useState(() => {
    // Default: today → today + 13 days (matches the ~2-week briefing horizon in the mock).
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    const end = new Date(today); end.setDate(today.getDate() + 13);
    return { from: iso(today), to: iso(end) };
  });

  const downloadEventOrder = async () => {
    if (!eoRange.from || !eoRange.to) { toast.error("Pick both from & to dates"); return; }
    if (eoRange.to < eoRange.from) { toast.error("`To` must be on or after `From`"); return; }
    setEoDownloading(true);
    try {
      const res = await api.get("/staff/reservations/event-order.pdf", {
        params: { from: eoRange.from, to: eoRange.to },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `TBB-EventOrder-${eoRange.from}_to_${eoRange.to}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Event order downloaded");
      setEoOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Blob responses on error need to be read back to text
      if (err?.response?.data instanceof Blob) {
        try {
          const txt = await err.response.data.text();
          const parsed = JSON.parse(txt);
          toast.error(parsed.detail || "Download failed");
        } catch { toast.error("Download failed"); }
      } else {
        toast.error(detail || "Download failed");
      }
    } finally { setEoDownloading(false); }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/staff/reservations");
      setItems(data);
    } catch (e) {
      toast.error("Could not load reservations.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const update = async (id, status) => {
    try {
      const { data } = await api.patch(`/staff/reservations/${id}/status`, { status });
      setItems((prev) => prev.map((r) => (r.id === id ? data : r)));
      toast.success(`Marked ${status}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const filtered = filter === "all" ? items : items.filter((r) => r.status === filter);

  const submitNew = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.date || !form.time) {
      toast.error("Name, phone, date and time are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        email: form.email || `no-email+${Date.now()}@tbb.local`,
        party_size: Number(form.party_size) || 2,
      };
      if (editingId) {
        // PATCH edit path — omit email if it was auto-generated blank
        const editPayload = { ...payload };
        if (!form.email) delete editPayload.email;
        const { data } = await api.patch(`/staff/reservations/${editingId}`, editPayload);
        setItems((prev) => prev.map((r) => (r.id === editingId ? data : r)));
        toast.success("Reservation updated");
        setEditingId(null);
        setCreating(false);
        setForm(EMPTY_FORM);
      } else {
        const { data: created } = await api.post("/reservations", payload);
        // Immediately mark as confirmed (staff added it, they already have the guest on phone)
        const { data: confirmed } = await api.patch(`/staff/reservations/${created.id}/status`, { status: "confirmed" });
        setItems((prev) => [confirmed, ...prev]);
        toast.success(`Booked ${form.name} · ${form.date} ${form.time}`);
        setForm(EMPTY_FORM);
        setCreating(false);
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save booking");
    } finally { setSaving(false); }
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setCreating(true);
    setForm({
      name: r.name || "",
      phone: r.phone || "",
      email: (r.email || "").startsWith("no-email+") ? "" : (r.email || ""),
      date: r.date || "",
      time: r.time || "19:30",
      party_size: r.party_size || 2,
      occasion: r.occasion || "",
      notes: r.notes || "",
      setup: r.setup || "TBC",
      confirm_by: r.confirm_by || "",
    });
  };

  const cancelForm = () => {
    setCreating(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const openAdvance = (r) => {
    setAdvTarget(r);
    if (r.advance) {
      setAdvForm({
        amount: String(r.advance.amount ?? ""),
        method: r.advance.method || "upi",
        reference: r.advance.reference || "",
        notes: r.advance.notes || "",
      });
    } else {
      setAdvForm(EMPTY_ADV);
    }
  };

  const closeAdvance = () => { setAdvTarget(null); setAdvForm(EMPTY_ADV); };

  const saveAdvance = async (e) => {
    e.preventDefault();
    if (!advTarget) return;
    const amt = Number(advForm.amount);
    if (!amt || amt <= 0) { toast.error("Amount must be greater than zero"); return; }
    setAdvSaving(true);
    try {
      const { data } = await api.post(`/staff/reservations/${advTarget.id}/advance`, {
        amount: amt,
        method: advForm.method,
        reference: advForm.reference,
        notes: advForm.notes,
      });
      setItems((prev) => prev.map((x) => (x.id === data.id ? data : x)));
      toast.success(`Advance recorded · ${data.advance?.receipt_no || ""}`);
      closeAdvance();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save advance");
    } finally { setAdvSaving(false); }
  };

  const clearAdvance = async () => {
    if (!advTarget) return;
    if (!window.confirm("Clear the advance record for this reservation? The receipt PDF will no longer be downloadable.")) return;
    setAdvSaving(true);
    try {
      const { data } = await api.delete(`/staff/reservations/${advTarget.id}/advance`);
      setItems((prev) => prev.map((x) => (x.id === data.id ? data : x)));
      toast.success("Advance cleared");
      closeAdvance();
    } catch {
      toast.error("Could not clear advance");
    } finally { setAdvSaving(false); }
  };

  const downloadReceipt = async (r) => {
    setReceiptDownloadId(r.id);
    try {
      const res = await api.get(`/staff/reservations/${r.id}/advance/receipt.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const rcpt = (r.advance?.receipt_no || "receipt").replace(/\//g, "-");
      a.download = `TBB-Advance-${rcpt}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download receipt");
    } finally {
      setReceiptDownloadId(null);
    }
  };

  return (
    <div data-testid="staff-reservations">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Reservations</div>
          <h1 className="mt-2 text-4xl">The diary</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex border border-[hsl(var(--rule))] rounded-full p-1 bg-[hsl(var(--bg-2))]">
            {["all", "pending", "confirmed", "declined", "cancelled"].map((f) => (
              <button
                key={f}
                data-testid={`filter-${f}`}
                onClick={() => setFilter(f)}
                className={`px-4 py-1.5 text-xs uppercase tracking-wider rounded-full transition-all ${
                  filter === f ? "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" : "text-[hsl(var(--ink-soft))]"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <button
            data-testid="event-order-btn"
            onClick={() => setEoOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded border border-[hsl(var(--forest))] text-[hsl(var(--forest))] hover:bg-[hsl(var(--forest))] hover:text-[hsl(var(--bg))] transition-colors"
            title="Download a printable kitchen & floor briefing across a date range"
          >
            <Download size={14} /> Event Order PDF
          </button>
          <button
            data-testid="new-res-btn"
            onClick={() => (creating ? cancelForm() : setCreating(true))}
            className="btn-brass !py-2 !px-4 text-sm"
          >
            <Plus size={14} /> {creating ? "Cancel" : "New booking"}
          </button>
        </div>
      </div>

      {eoOpen && (
        <div data-testid="event-order-popover" className="mt-6 p-5 border border-[hsl(var(--forest))] bg-[hsl(var(--bg-2))]">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="eyebrow text-[hsl(var(--forest))]">Kitchen & Floor Briefing</div>
              <h3 className="mt-1 text-lg font-semibold">Event Order — pick a window</h3>
              <p className="mt-1 text-xs text-[hsl(var(--ink-soft))] max-w-lg">
                Prints every pending & confirmed booking in the range with stat cards (bookings, covers, double-setup nights, enquiries, free days). Cancelled bookings are excluded. Max window: 62 days.
              </p>
            </div>
            <button onClick={() => setEoOpen(false)} className="text-[hsl(var(--ink-soft))]" aria-label="Close">
              <X size={16} />
            </button>
          </div>
          <div className="mt-4 flex flex-wrap items-end gap-3">
            <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
              From
              <input
                type="date"
                data-testid="event-order-from"
                value={eoRange.from}
                onChange={(e) => setEoRange({ ...eoRange, from: e.target.value })}
                className="tbb-inp mt-1 block"
              />
            </label>
            <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
              To
              <input
                type="date"
                data-testid="event-order-to"
                value={eoRange.to}
                onChange={(e) => setEoRange({ ...eoRange, to: e.target.value })}
                className="tbb-inp mt-1 block"
              />
            </label>
            <button
              data-testid="event-order-download"
              disabled={eoDownloading}
              onClick={downloadEventOrder}
              className="btn-brass !py-2.5 !px-5 text-sm inline-flex items-center gap-2"
            >
              <Download size={13} /> {eoDownloading ? "Building…" : "Download PDF"}
            </button>
          </div>
          <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.55rem 0.75rem;font-size:0.9rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
        </div>
      )}

      {creating && (
        <form data-testid="new-res-form" onSubmit={submitNew} className="mt-6 border border-[hsl(var(--brass))] bg-[hsl(var(--bg-2))] p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2 text-sm text-[hsl(var(--ink-soft))]">
            {editingId ? (
              <>Editing reservation. Changes save immediately without altering the current status.</>
            ) : (
              <>Add a booking taken over phone / WhatsApp. It goes straight to <b>Confirmed</b>.</>
            )}
          </div>
          <input required data-testid="new-name" placeholder="Guest name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="tbb-inp" />
          <input required data-testid="new-phone" placeholder="Phone *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="tbb-inp" />
          <input data-testid="new-email" placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="tbb-inp" />
          <input required type="date" data-testid="new-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="tbb-inp" />
          <input required type="time" data-testid="new-time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="tbb-inp" />
          <input required type="number" min="1" max="200" data-testid="new-pax" placeholder="Party size *" value={form.party_size} onChange={(e) => setForm({ ...form, party_size: e.target.value })} className="tbb-inp" />
          <input data-testid="new-occasion" placeholder="Occasion (birthday, anniversary…)" value={form.occasion} onChange={(e) => setForm({ ...form, occasion: e.target.value })} className="tbb-inp" />
          <select
            data-testid="new-setup"
            value={form.setup || "TBC"}
            onChange={(e) => setForm({ ...form, setup: e.target.value })}
            className="tbb-inp"
            title="Where the guest will be seated — surfaces on the Event Order PDF briefing"
          >
            <option value="TBC">Setup — TBC</option>
            <option value="Inside">Setup — Inside</option>
            <option value="Outside">Setup — Outside</option>
          </select>
          <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))] md:col-span-2 flex items-center gap-3 flex-wrap">
            <span className="whitespace-nowrap">Confirm by (optional)</span>
            <input
              type="date"
              data-testid="new-confirm-by"
              value={form.confirm_by || ""}
              onChange={(e) => setForm({ ...form, confirm_by: e.target.value })}
              className="tbb-inp"
              title="Only meaningful while the booking is an enquiry — shows as 'confirm before…' on the Event Order PDF"
            />
            {form.confirm_by && (
              <button
                type="button"
                onClick={() => setForm({ ...form, confirm_by: "" })}
                className="text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))] hover:text-[hsl(6_62%_45%)]"
              >
                Clear
              </button>
            )}
          </label>
          <textarea data-testid="new-notes" placeholder="Notes (allergies, seating, package…)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="tbb-inp md:col-span-2" />
          <div className="md:col-span-2 flex gap-3">
            <button data-testid="new-res-save" disabled={saving} className="btn-brass !py-2.5 !px-5 text-sm">
              {saving ? "Saving..." : (editingId ? "Save changes" : "Save & confirm")}
            </button>
            <button type="button" onClick={cancelForm} className="text-sm text-[hsl(var(--ink-soft))]">Discard</button>
          </div>
          <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.55rem 0.75rem;font-size:0.9rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
        </form>
      )}

      <div className="mt-8 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
        {loading && <div className="p-6 text-[hsl(var(--ink-soft))]">Loading...</div>}
        {!loading && filtered.length === 0 && (
          <div data-testid="reservations-empty" className="p-10 text-center text-[hsl(var(--ink-soft))]">
            Nothing in this bucket yet.
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <ul className="divide-y divide-[hsl(var(--rule))]">
            {filtered.map((r) => (
              <li key={r.id} data-testid={`reservation-${r.id}`} className="p-5 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-start">
                <div>
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <div className="text-lg font-semibold">{r.name}</div>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${STATUS_STYLES[r.status] || ""}`}>{r.status}</span>
                    <div className="text-sm text-[hsl(var(--ink-soft))]">
                      {r.date} · {r.time} · {r.party_size} pax
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-[hsl(var(--ink-soft))]">
                    {r.phone} · {r.email}
                    {r.occasion && <> · Occasion: <span className="text-[hsl(var(--ink))]">{r.occasion}</span></>}
                  </div>
                  {r.notes && (
                    <div className="mt-1 text-sm italic text-[hsl(var(--ink-soft))]">&ldquo;{r.notes}&rdquo;</div>
                  )}
                  {r.advance ? (
                    <div data-testid={`advance-badge-${r.id}`} className="mt-2 inline-flex items-center gap-2 text-xs px-2.5 py-1 rounded bg-[hsl(148_40%_92%)] text-[hsl(var(--forest))] border border-[hsl(var(--forest)/0.3)]">
                      <IndianRupee size={11} />
                      <span className="font-semibold">₹{Number(r.advance.amount).toLocaleString("en-IN")}</span>
                      <span className="opacity-75">advance · {(r.advance.method || "").toUpperCase()}</span>
                      <span className="opacity-60 font-mono">{r.advance.receipt_no}</span>
                    </div>
                  ) : (r.party_size > 20 && (
                    <div className="mt-2 inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded bg-[hsl(var(--brass)/0.12)] text-[hsl(var(--brass-2))] border border-[hsl(var(--brass)/0.4)]">
                      <IndianRupee size={11} /> 50% advance required (party &gt; 20)
                    </div>
                  ))}
                  <div data-testid={`res-meta-${r.id}`} className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-[hsl(var(--ink-soft))]">
                    <span>Booked <span className="text-[hsl(var(--ink))]">{fmtTs(r.created_at)}</span></span>
                    {r.updated_at && new Date(r.updated_at).getTime() - new Date(r.created_at).getTime() > 5_000 && (
                      <span data-testid={`res-updated-${r.id}`}>· Last edited <span className="text-[hsl(var(--ink))]">{fmtTs(r.updated_at)}</span></span>
                    )}
                    {r.status === "declined" && r.declined_at && (
                      <span data-testid={`res-declined-${r.id}`}>· Declined <span className="text-[hsl(6_62%_45%)]">{fmtTs(r.declined_at)}</span></span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {canConfirm(r) && (
                    <button
                      data-testid={`confirm-${r.id}`}
                      onClick={() => update(r.id, "confirmed")}
                      title="Confirm booking"
                      className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded bg-[hsl(var(--forest))] text-[hsl(var(--bg))]"
                    >
                      <Check size={12} /> Confirm
                    </button>
                  )}
                  {r.status === "declined" && !canConfirm(r) && (
                    <button
                      data-testid={`reopen-${r.id}`}
                      onClick={() => update(r.id, "pending")}
                      title="Reopen declined booking (marks it pending)"
                      className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded border border-[hsl(var(--brass))] text-[hsl(var(--brass-2))] hover:bg-[hsl(var(--brass))] hover:text-[hsl(var(--bg))] transition-colors"
                    >
                      <RotateCcw size={12} /> Reopen
                    </button>
                  )}
                  <button
                    data-testid={`whatsapp-${r.id}`}
                    onClick={async () => {
                      const kind = r.status === "declined" ? "decline" : "confirmation";
                      const label = kind === "decline" ? "decline note" : "confirmation";
                      if (!window.confirm(`Send WhatsApp ${label} to ${r.name}?`)) return;
                      try {
                        const { data } = await api.post(`/staff/reservations/${r.id}/whatsapp`, { kind });
                        toast.success(`WhatsApp ${label} sent · ${data.status || "queued"}`);
                      } catch (err) {
                        const d = err?.response?.data?.detail;
                        toast.error(typeof d === "string" ? d : "Send failed — check WhatsApp Log");
                      }
                    }}
                    title={r.status === "declined" ? "Send apology / decline note via WhatsApp" : "Send confirmation WhatsApp via Twilio"}
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded border border-[hsl(var(--brass))] text-[hsl(var(--brass-2))] hover:bg-[hsl(var(--brass))] hover:text-[hsl(var(--bg))] transition-colors"
                  >
                    <MessageCircle size={12} /> {r.status === "declined" ? "Send decline note" : "Send WhatsApp"}
                  </button>
                  <Link
                    to={`/staff/reservations/${r.id}/print`}
                    data-testid={`print-${r.id}`}
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded border border-[hsl(var(--rule))] text-[hsl(var(--ink))]"
                  >
                    <Printer size={12} /> Print
                  </Link>
                  <button
                    data-testid={`edit-${r.id}`}
                    onClick={() => startEdit(r)}
                    title="Edit reservation"
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded border border-[hsl(var(--ink))] text-[hsl(var(--ink))] hover:bg-[hsl(var(--ink))] hover:text-[hsl(var(--bg))] transition-colors"
                  >
                    <Pencil size={12} /> Edit
                  </button>
                  <button
                    data-testid={`advance-${r.id}`}
                    onClick={() => openAdvance(r)}
                    title={r.advance ? "Edit or clear advance" : "Record an advance payment"}
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded border border-[hsl(var(--brass))] text-[hsl(var(--brass-2))] hover:bg-[hsl(var(--brass))] hover:text-[hsl(var(--bg))] transition-colors"
                  >
                    <IndianRupee size={12} /> {r.advance ? "Edit advance" : "Advance"}
                  </button>
                  {r.advance && (
                    <button
                      data-testid={`receipt-${r.id}`}
                      onClick={() => downloadReceipt(r)}
                      disabled={receiptDownloadId === r.id}
                      title={`Download receipt ${r.advance.receipt_no}`}
                      className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded border border-[hsl(var(--forest))] text-[hsl(var(--forest))] hover:bg-[hsl(var(--forest))] hover:text-[hsl(var(--bg))] transition-colors disabled:opacity-50 disabled:cursor-wait"
                    >
                      <FileText size={12} /> {receiptDownloadId === r.id ? "Preparing..." : "Receipt"}
                    </button>
                  )}
                  <button
                    data-testid={`pending-${r.id}`}
                    disabled={r.status === "pending"}
                    onClick={() => update(r.id, "pending")}
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded border border-[hsl(var(--rule))] text-[hsl(var(--ink))] disabled:opacity-40"
                  >
                    <Clock size={12} /> Pending
                  </button>
                  <button
                    data-testid={`decline-${r.id}`}
                    disabled={r.status === "declined"}
                    onClick={() => update(r.id, "declined")}
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded border border-[hsl(6_62%_45%)] text-[hsl(6_62%_45%)] disabled:opacity-40"
                  >
                    <X size={12} /> Decline
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {advTarget && (
        <div
          data-testid="advance-modal"
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) closeAdvance(); }}
        >
          <form
            onSubmit={saveAdvance}
            className="bg-[hsl(var(--bg))] border border-[hsl(var(--rule))] w-full max-w-lg p-6 shadow-xl"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="eyebrow">Advance payment</div>
                <h2 className="mt-1 text-2xl">{advTarget.name}</h2>
                <div className="text-sm text-[hsl(var(--ink-soft))] mt-1">
                  {advTarget.date} · {advTarget.time} · {advTarget.party_size} pax
                </div>
                {advTarget.advance?.receipt_no && (
                  <div className="mt-2 text-xs font-mono text-[hsl(var(--brass-2))]">
                    Receipt: {advTarget.advance.receipt_no}
                  </div>
                )}
              </div>
              <button type="button" onClick={closeAdvance} className="text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--ink))]">
                <X size={18} />
              </button>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3">
              <label className="col-span-2 text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Amount (₹) *
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--ink-soft))]">₹</span>
                  <input
                    required
                    data-testid="advance-amount"
                    type="number"
                    min="1"
                    step="0.01"
                    value={advForm.amount}
                    onChange={(e) => setAdvForm({ ...advForm, amount: e.target.value })}
                    placeholder="e.g. 12500"
                    className="tbb-inp !pl-7 w-full text-base"
                    autoFocus
                  />
                </div>
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Method *
                <select
                  data-testid="advance-method"
                  value={advForm.method}
                  onChange={(e) => setAdvForm({ ...advForm, method: e.target.value })}
                  className="tbb-inp mt-1 w-full"
                >
                  {METHOD_OPTIONS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Reference / Txn ID
                <input
                  data-testid="advance-reference"
                  value={advForm.reference}
                  onChange={(e) => setAdvForm({ ...advForm, reference: e.target.value })}
                  placeholder="UPI ref / UTR / cheque no."
                  className="tbb-inp mt-1 w-full"
                />
              </label>
              <label className="col-span-2 text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Notes
                <textarea
                  data-testid="advance-notes"
                  value={advForm.notes}
                  onChange={(e) => setAdvForm({ ...advForm, notes: e.target.value })}
                  rows={2}
                  placeholder="e.g. 50% of ₹25,000 for 25-pax birthday dinner"
                  className="tbb-inp mt-1 w-full resize-none"
                />
              </label>
            </div>

            <p className="mt-4 text-xs text-[hsl(var(--ink-soft))] border-l-2 border-[hsl(var(--brass))] pl-3">
              A branded PDF receipt will be generated. You can download or share it with the guest after saving.
            </p>

            <div className="mt-5 flex items-center justify-between gap-3">
              <div>
                {advTarget.advance && (
                  <button
                    type="button"
                    data-testid="advance-clear"
                    onClick={clearAdvance}
                    disabled={advSaving}
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded border border-[hsl(6_62%_45%)] text-[hsl(6_62%_45%)] hover:bg-[hsl(6_62%_45%)] hover:text-[hsl(var(--bg))] transition-colors disabled:opacity-40"
                  >
                    <Trash2 size={12} /> Clear advance
                  </button>
                )}
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={closeAdvance} className="text-sm text-[hsl(var(--ink-soft))] px-3">
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="advance-save"
                  disabled={advSaving}
                  className="btn-brass !py-2.5 !px-5 text-sm"
                >
                  {advSaving ? "Saving..." : advTarget.advance ? "Update advance" : "Record & generate receipt"}
                </button>
              </div>
            </div>
            <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.55rem 0.75rem;font-size:0.9rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
          </form>
        </div>
      )}
    </div>
  );
}
